
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../../services/toast.config";

import type { AppDispatch } from "../../../core/data/redux/store";
import {
  selectReferrals,
  selectReferralsLoading,
  selectJobs,
} from "../../../core/data/redux/recruitment/selectors";
import {
  fetchHRReferrals,
  fetchJobs,
  updateReferralStatus,
  convertReferralToApplication,
} from "../../../core/data/redux/recruitment/thunks";

import ReferralFilters, {
  type ReferralFiltersState,
} from "./components/ReferralFilters";
import ReferralTable from "./components/ReferralTable";
import ReferralForm from "./components/ReferralForm";
import ReferralRewardsDashboard from "./components/ReferralRewardsDashboard";
import { ApproveBonusModal, ProcessPaymentModal } from "./components/ApproveBonusModal";
import RecruitmentHeader from "../components/RecruitmentHeader";
import Footer from "../components/Footer";
import type { Referral } from "../../../core/data/redux/recruitment/types";
import { approveReferralBonus, processReferralPayment } from "../../../core/data/redux/recruitment/thunks";

const ReferralManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [activeTab, setActiveTab] = useState<"referrals" | "rewards">("referrals");

  const referrals = useSelector(selectReferrals);
  const loading = useSelector(selectReferralsLoading);
  const jobs = useSelector(selectJobs);
  const activeCompany = useSelector((state: any) => state.recruitment.activeCompany);

  const [filters, setFilters] = useState<ReferralFiltersState>({
    search: "",
    status: "",
    date_from: undefined,
    date_to: undefined,
  });
  const [showForm, setShowForm] = useState(false);

  
  const [showApproveBonus, setShowApproveBonus] = useState(false);
  const [showProcessPayment, setShowProcessPayment] = useState(false);
  const [activeReferral, setActiveReferral] = useState<Referral | null>(null);

  
  useEffect(() => {
    if (!activeCompany?.company_id) return;
    if (activeTab === "referrals") {
      dispatch(fetchHRReferrals());
      dispatch(fetchJobs({ limit: 100 }));
    }
  }, [dispatch, activeCompany?.company_id, activeTab]);

  const handleFilterChange = useCallback(
    (partial: Partial<ReferralFiltersState>) => {
      const next = { ...filters, ...partial };
      setFilters(next);
      
      if ("status" in partial || "date_from" in partial || "date_to" in partial) {
        dispatch(
          fetchHRReferrals({
            status: next.status || undefined,
            date_from: next.date_from,
            date_to: next.date_to,
          }),
        );
      }
    },
    [dispatch, filters],
  );

  
  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return referrals;
    return referrals.filter(
      (r) =>
        r.candidate_name?.toLowerCase().includes(q) ||
        r.candidate_email?.toLowerCase().includes(q) ||
        r.referrer_name?.toLowerCase().includes(q) ||
        r.job_title?.toLowerCase().includes(q),
    );
  }, [referrals, filters.search]);

  const handleUpdateStatus = useCallback(
    async (referralId: number, status: string) => {
      try {
        await dispatch(updateReferralStatus({ referralId, status })).unwrap();
        notify.success("Status updated");
        dispatch(fetchHRReferrals());
      } catch (err: any) {
        notify.error(typeof err === "string" ? err : "Failed to update status");
      }
    },
    [dispatch],
  );

  const handleConvert = useCallback(
    async (referralId: number) => {
      try {
        await dispatch(convertReferralToApplication(referralId)).unwrap();
        notify.success("Converted to application!");
        dispatch(fetchHRReferrals());
      } catch (err: any) {
        notify.error(typeof err === "string" ? err : "Conversion failed");
      }
    },
    [dispatch],
  );

  const handleReferralSubmitted = useCallback(() => {
    dispatch(fetchHRReferrals());
  }, [dispatch]);

  const handleApproveBonusSubmit = async (referralId: number, bonusAmount: number, approvalNotes?: string) => {
    try {
      await dispatch(approveReferralBonus({ referralId, bonusAmount, approvalNotes })).unwrap();
      notify.success("Bonus approved successfully!");
      dispatch(fetchHRReferrals());
    } catch (err: any) {
      notify.error(typeof err === "string" ? err : "Failed to approve bonus");
      throw err;
    }
  };

  const handleProcessPaymentSubmit = async (referralId: number, paymentReference: string, paymentDate: string, paymentNotes?: string) => {
    try {
      await dispatch(processReferralPayment({ referralId, paymentReference, paymentDate, paymentNotes })).unwrap();
      notify.success("Bonus payment processed!");
      dispatch(fetchHRReferrals());
    } catch (err: any) {
      notify.error(typeof err === "string" ? err : "Failed to process payment");
      throw err;
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        
        <RecruitmentHeader title="Referrals" breadcrumb="Referrals" />

        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid #f0f0f0",
              padding: "0 20px",
              gap: 24,
            }}
          >
            {[
              { key: "referrals", label: "Referral Management", icon: <i className="ti ti-users" /> },
              { key: "rewards", label: "Rewards & Payouts", icon: <i className="ti ti-gift" /> },
            ].map(({ key, label, icon }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as any)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "14px 12px",
                    background: "transparent",
                    border: "none",
                    borderBottom: isActive
                      ? "2px solid #1677ff"
                      : "2px solid transparent",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontWeight: isActive ? 600 : 400,
                    fontSize: 14,
                    color: isActive ? "#1677ff" : "#595959",
                    transition: "all 0.2s",
                    marginBottom: -1, 
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      color: isActive ? "#1677ff" : "#8c8c8c",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {icon}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>

          <div style={{ padding: 20 }}>
            {activeTab === "referrals" ? (
              <>
                <ReferralFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  loading={loading}
                  onReferClick={() => setShowForm(true)}
                />
                <div className="mt-4">
                  <ReferralTable
                    data={filtered}
                    loading={loading}
                    onConvert={handleConvert}
                    onUpdateStatus={handleUpdateStatus}
                    onApproveBonus={(id) => {
                      const ref = referrals.find((r) => r.id === id);
                      if (ref) {
                        setActiveReferral(ref);
                        setShowApproveBonus(true);
                      }
                    }}
                    onProcessPayment={(id) => {
                      const ref = referrals.find((r) => r.id === id);
                      if (ref) {
                        setActiveReferral(ref);
                        setShowProcessPayment(true);
                      }
                    }}
                  />
                </div>
              </>
            ) : (
              <ReferralRewardsDashboard />
            )}
          </div>
        </div>
      </div>

      
      <ReferralForm
        show={showForm}
        onClose={() => setShowForm(false)}
        jobs={jobs}
        onSubmitted={handleReferralSubmitted}
      />

      <ApproveBonusModal
        show={showApproveBonus}
        onClose={() => setShowApproveBonus(false)}
        onSubmit={handleApproveBonusSubmit}
        referral={activeReferral}
      />

      <ProcessPaymentModal
        show={showProcessPayment}
        onClose={() => setShowProcessPayment(false)}
        onSubmit={handleProcessPaymentSubmit}
        referral={activeReferral}
      />

      <Footer />
    </div>
  );
};

export default ReferralManagement;
